import { createPortal } from 'react-dom';
import AiUsageNotice from '@/components/ai/AiUsageNotice';
import { Danger, ArrowLeft2, ArrowRight2, Refresh, Cross } from '@/components/icons';
import { AISectionSkeleton } from '@/components/preview/AISectionSkeleton';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Button } from '@/components/ui/button';
import AIGenerateButton from '@/components/wizard/AIGenerateButton';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { cn } from '@/lib/utils';
import { AI_PRESETS } from './constants';
import type { FloatingAIPanelProps } from './types';

export default function FloatingAIPanel({
    mode,
    isAIPanelRendered,
    isAIPanelOpen,
    isAIPanelVisible,
    aiPanelPosition,
    aiPanelRef,
    aiPrompt,
    aiStatus,
    aiError,
    streamedText,
    sanitizedStreamedText,
    generationCount,
    currentGenerationIndex,
    currentPresetLabel,
    aiUiFontStyle,
    onClose,
    onGenerate,
    onCancel,
    onApply,
    onRegenerate,
    onPresetSelect,
    onNavigateHistory,
}: FloatingAIPanelProps) {
    const aiCreditAccess = useFeatureEntitlement('ai.credits');

    if (!isAIPanelRendered) return null;

    const isPromptPhase = aiStatus === 'prompt';
    const isGenerating = aiStatus === 'streaming';
    const isReview = aiStatus === 'review';
    const hasMultipleGenerations = generationCount > 1;

    const panelContent = (
        <div
            ref={(node) => {
                aiPanelRef.current = node;
            }}
            className={cn(
                'rounded-xl border border-blue-200 bg-card',
                'space-y-3 p-4',
                mode === 'inline' ? 'shadow-sm' : 'shadow-2xl',
                mode === 'inline'
                    ? isAIPanelOpen
                        ? 'block'
                        : 'hidden'
                    : cn(
                          'transition-[opacity,transform] duration-200 ease-out will-change-transform',
                          isAIPanelOpen ? 'pointer-events-auto' : 'pointer-events-none',
                          isAIPanelVisible
                              ? 'translate-y-0 scale-100 opacity-100'
                              : aiPanelPosition.placement === 'top'
                                ? '-translate-y-1.5 scale-[0.98] opacity-0'
                                : 'translate-y-1.5 scale-[0.98] opacity-0',
                          aiPanelPosition.isMobile
                              ? 'fixed inset-x-4 bottom-4 max-h-[65vh]'
                              : 'fixed w-[420px] max-w-[calc(100vw-24px)]',
                      ),
            )}
            style={
                mode === 'inline'
                    ? aiUiFontStyle
                    : aiPanelPosition.isMobile
                      ? {
                            ...aiUiFontStyle,
                            visibility: isAIPanelVisible ? 'visible' : 'hidden',
                        }
                      : {
                            ...aiUiFontStyle,
                            top: aiPanelPosition.top,
                            left: aiPanelPosition.left,
                            transformOrigin:
                                aiPanelPosition.placement === 'top' ? 'bottom right' : 'top right',
                            visibility: isAIPanelVisible ? 'visible' : 'hidden',
                        }
            }
            data-placement={aiPanelPosition.placement}
        >
            <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    Assistente de escrita
                </p>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="size-7 p-0 text-muted-foreground"
                    aria-label="Fechar IA"
                    style={aiUiFontStyle}
                >
                    <Cross className="size-4" />
                </Button>
            </div>

            {isGenerating && <AISectionSkeleton visible />}

            {isGenerating && (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onCancel}
                    className="w-full border-border text-xs text-muted-foreground hover:bg-surface"
                    style={aiUiFontStyle}
                >
                    Cancelar
                </Button>
            )}

            {isReview && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {hasMultipleGenerations && (
                            <div className="flex items-center gap-0.5">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onNavigateHistory('prev')}
                                    disabled={currentGenerationIndex === 0}
                                    className="size-6 p-0 text-muted-foreground"
                                    aria-label="Geração anterior"
                                    style={aiUiFontStyle}
                                >
                                    <ArrowLeft2 className="size-3.5" />
                                </Button>
                                <span className="min-w-[2.5rem] text-center text-xs tabular-nums text-muted-foreground">
                                    {currentGenerationIndex + 1}/{generationCount}
                                </span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onNavigateHistory('next')}
                                    disabled={currentGenerationIndex === generationCount - 1}
                                    className="size-6 p-0 text-muted-foreground"
                                    aria-label="Próxima geração"
                                    style={aiUiFontStyle}
                                >
                                    <ArrowRight2 className="size-3.5" />
                                </Button>
                            </div>
                        )}
                    </div>

                    {currentPresetLabel && (
                        <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                            {currentPresetLabel}
                        </span>
                    )}
                </div>
            )}

            {isReview && (
                <div className="max-h-[28vh] overflow-y-auto rounded-md border border-blue-100 bg-card p-3 text-sm text-foreground">
                    <div
                        className="prose prose-sm max-w-none prose-p:my-1 prose-ol:list-decimal prose-ol:pl-6 prose-ul:list-disc prose-ul:pl-6"
                        dangerouslySetInnerHTML={{
                            __html: sanitizedStreamedText,
                        }}
                    />
                </div>
            )}

            {isPromptPhase && aiError && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                    <Danger className="mt-0.5 size-3.5 shrink-0" />
                    <span>{aiError}</span>
                </div>
            )}

            {isPromptPhase && (
                <div className="flex flex-wrap gap-1.5">
                    {AI_PRESETS.map((preset) => {
                        const isSelected = aiPrompt === preset.key;
                        return (
                            <Button
                                key={preset.key}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onPresetSelect(preset.key)}
                                className={cn(
                                    'h-7 gap-1.5 text-xs',
                                    isSelected
                                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                                        : 'border-blue-100 text-muted-foreground hover:bg-blue-50 hover:text-blue-700',
                                )}
                                style={aiUiFontStyle}
                            >
                                <preset.icon className="size-3.5" />
                                {preset.label}
                            </Button>
                        );
                    })}
                </div>
            )}

            {isPromptPhase && <AiUsageNotice tone="inline" />}

            {isPromptPhase && aiCreditAccess.shouldBlock && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                    <span>{getEntitlementBlockedMessage(aiCreditAccess.decision)}</span>
                    {aiCreditAccess.isUnavailableInPlan && aiCreditAccess.availableFromPlan ? (
                        <FeatureUnavailableChip availablePlan={aiCreditAccess.availableFromPlan} />
                    ) : null}
                </div>
            )}

            {isPromptPhase && (
                <AIGenerateButton
                    onClick={() => void onGenerate()}
                    disabled={!aiPrompt.trim() || aiCreditAccess.shouldBlock}
                />
            )}

            {isReview && (
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onRegenerate}
                        className="flex-1 gap-2"
                        style={aiUiFontStyle}
                    >
                        <Refresh className="size-3.5" />
                        Regenerar
                    </Button>
                    <Button
                        type="button"
                        onClick={onApply}
                        disabled={!streamedText}
                        className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                        style={aiUiFontStyle}
                    >
                        Aplicar
                    </Button>
                </div>
            )}
        </div>
    );

    if (mode === 'inline') {
        return panelContent;
    }

    return createPortal(
        <div className="force-light pointer-events-none fixed inset-0 z-[80]">
            {aiPanelPosition.isMobile && isAIPanelOpen && (
                <button
                    type="button"
                    aria-label="Fechar assistente"
                    className="pointer-events-auto absolute inset-0 bg-black/20"
                    onClick={onClose}
                />
            )}

            {panelContent}
        </div>,
        document.body,
    );
}
